type UuidType = 'ItemType' | 'PropertyType' | 'ReferenceType' | 'PropertySet' | 'ReferenceSet' | 'ReferencePropertySet' | 'ValueSet' | 'Value';
type UuidPlaceholders = {
    Origin: string,
    Module: string,
    Schema: string,
    ItemTypeName?: string,
    PropertyTypeName?: string,
    ReferenceTypeName?: string,
    ItemTypeNameSource?: string,
    ItemTypeNameDestination?: string,
    ValueSetName?: string,
    Value?: string
};
type UuidPlaceholderKeys = keyof UuidPlaceholders;
type UuidSchemaPart = {
    schema : string;
    parsed : string;
    placeholder : string;
    replacement : string;
    transformation : 'none' | 'lower' | 'upper';
    optional? : boolean;
};
type UuidSchema = {
    schema : string;
    parsed : string;
    parts : {
        [ id : string ] : UuidSchemaPart
    };
};
type Uuid = {
    uuid : string;
    success : boolean;
    error : string;
}
// eslint-disable-next-line no-new-object
export default {

    module    : 'base' as string,
    separator : ':' as string,
    uuidSchema: {
        ItemType            : '<Origin>:<Module>:<lower(Schema)>:ItemType:<ItemTypeName>',
        PropertyType        : '<Origin>:<Module>:<lower(Schema)>:PropertyType:<PropertyTypeName>',
        ReferenceType       : '<Origin>:<Module>:<lower(Schema)>:ReferenceType:<ReferenceTypeName>',
        PropertySet         : '<Origin>:<Module>:<lower(Schema)>:PropertySet:<ItemTypeName>:<PropertyTypeName>',
        ReferenceSet        : '<Origin>:<Module>:<lower(Schema)>:ReferenceSet:<ItemTypeNameSource>:<ReferenceTypeName>:<ItemTypeNameDestination>',
        ReferencePropertySet: '<Origin>:<Module>:<lower(Schema)>:ReferencePropertySet:<ItemTypeNameSource>:<ReferenceTypeName>:<ItemTypeNameDestination>:<PropertyTypeName>',
        ValueSet            : '<Origin>:<Module>:<lower(Schema)>:ValueSet:<ValueSetName>',
        Value               : '<Origin>:<Module>:<lower(Schema)>:Value:<ValueSetName>:<Value>'
    },

    /**
     *
     * @param {UuidType} type
     * @param {UuidPlaceholders} placeholders
     * @param {Boolean} [allowEmptyMandatoryPlaceholders=false]
     *
     * @return {String}
     *
     * @private
     */
    getUuidByTypeAndPlaceholders: function( type : UuidType, placeholders : UuidPlaceholders, allowEmptyMandatoryPlaceholders : boolean = false ) : string {
        if ( !placeholders[ 'Origin' ] ) {
            placeholders[ 'Origin' ] = config.common.uuidOrigin;
        }
        if ( !placeholders[ 'Module' ] ) {
            placeholders[ 'Module' ] = this.module;
        }

        const result = this._computeUuid( this.uuidSchema[ type ], placeholders, allowEmptyMandatoryPlaceholders );

        if ( result[ 'success' ] ) {
            return result[ 'uuid' ];
        }
        else {
            throw ( new Error( result[ 'error' ] ) );
        }
    },

    /**
     *
     * @param {String} uuidSchema
     * @param {Object} placeholders
     * @param {Boolean} [allowEmptyMandatoryPlaceholders=false]
     *
     * @return {{success: boolean, error: string, uuid: string}}
     *
     * @private
     */
    _computeUuid: function( uuidSchema : string, placeholders : UuidPlaceholders, allowEmptyMandatoryPlaceholders : boolean = false ) : Uuid {
        const retVal : Uuid    = {
                  uuid   : '',
                  success: false,
                  error  : ''
              },
              parsedUuidSchema = this._parseUuidSchema( uuidSchema );

        if ( parsedUuidSchema ) {
            const partsIds = Object.keys( parsedUuidSchema.parts ) as string[];

            retVal.uuid = parsedUuidSchema.parsed;

            // process parts
            for ( const id of partsIds ) {
                const config = parsedUuidSchema.parts[ id ];

                let replacement : string = '';

                if ( !retVal.error ) {
                    const placeholder = placeholders[ config.replacement as UuidPlaceholderKeys ];
                    if ( placeholder ) {
                        switch ( config.transformation ) {
                            case 'upper':
                                replacement = placeholder.toUpperCase();
                                break;

                            case 'lower':
                                replacement = placeholder.toLowerCase();
                                break;

                            case 'none':
                            default:
                                replacement = placeholder;
                                break;
                        }

                        replacement = replacement.replace( ' ', '_' );
                        replacement = config.parsed.replace( config.placeholder, replacement );
                    }
                    else if ( config.optional || allowEmptyMandatoryPlaceholders ) {
                        replacement = '';
                    }
                    else {
                        retVal.uuid = '';
                        retVal.error = `Error: mandatory schema part "${config.replacement}" is missing`;
                    }

                    retVal.uuid = retVal.uuid.replace( id, replacement );
                }
            }

            retVal.success = true;
        }
        else {
            retVal.error = 'Couldn\'t parse UUID schema';
        }

        return retVal;
    },

    /**
     * Parses the uuid schema.
     *
     * @param {string} nameSchema
     *
     * @return {UuidSchema}
     *
     * @private
     */
    _parseUuidSchema: function( nameSchema : string ) : UuidSchema {
        const retVal : UuidSchema = {
            schema: nameSchema,
            parsed: nameSchema,
            parts : {}
        };

        let matches : string[],
            id : string;

        // get optional parts
        matches = nameSchema.match( /\[.*?]/g ) ?? []
        for ( let i = 0; i < matches.length; i++ ) {
            id = '::o:' + i + '::';

            retVal.parsed = retVal.parsed.replace( matches[ i ], id );
            retVal.parts[ id ] = this._parseUuidSchemaPart( matches[ i ] );
            retVal.parts[ id ].optional = true;
        }

        // get mandatory parts
        matches = retVal.parsed.match( /<.*?>/g ) ?? [];
        for ( let i = 0; i < matches.length; i++ ) {
            id = '::m:' + i + '::';

            retVal.parsed = retVal.parsed.replace( matches[ i ], id );
            retVal.parts[ id ] = this._parseUuidSchemaPart( matches[ i ] );
            retVal.parts[ id ].optional = false;
        }

        return retVal;
    },

    /**
     * Parses a part of the uuid schema.
     *
     * @param {string} uuidSchemaPart
     *
     * @return {UuidSchemaPart}
     *
     * @private
     */
    _parseUuidSchemaPart: function( uuidSchemaPart : string ) : UuidSchemaPart {
        const retVal : UuidSchemaPart = {
            schema        : uuidSchemaPart,
            parsed        : '',
            placeholder   : '',
            replacement   : '',
            transformation: 'none'
        };

        let parts,
            subParts;

        // remove []
        uuidSchemaPart = uuidSchemaPart.replace( /[\[\]]/g, '' );

        if ( ( parts = uuidSchemaPart.match( /<(.*?)>/ ) ) ) {
            retVal.placeholder = '::t::';

            // get replacement and transformation if
            if ( ( subParts = parts[ 1 ].match( /(.*?)\((.*?)\)/ ) ) ) {
                retVal.transformation = subParts[ 1 ].toLowerCase() as UuidSchemaPart[ 'transformation' ];
                retVal.replacement = subParts[ 2 ];
            }
            else {
                retVal.replacement = parts[ 1 ];
            }

            // get schema
            retVal.parsed = uuidSchemaPart.replace( parts[ 0 ], retVal.placeholder );
        }

        return retVal;
    }
};
